import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, MapPin, Clock, Star, Calendar, User, Phone, Navigation } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Trip {
  id: string;
  pickup_location: string;
  dropoff_location: string;
  distance: number;
  fare: number;
  status: 'completed' | 'cancelled' | 'in_progress';
  rating?: number;
  created_at: string;
}

interface Profile {
  full_name: string;
  phone: string;
  share_location: boolean;
  last_location_update?: string;
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [stats, setStats] = useState({
    totalTrips: 0,
    totalDistance: 0,
    totalSpent: 0,
    averageRating: 0
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/giris');
        return;
      }

      // Profil bilgilerini yükle
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        setProfile(profile);
      }

      // Yolculukları yükle
      const { data: trips } = await supabase
        .from('trips')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (trips) {
        setTrips(trips);

        // İstatistikleri hesapla
        const stats = trips.reduce((acc, trip) => {
          if (trip.status === 'completed') {
            acc.totalTrips++;
            acc.totalDistance += trip.distance || 0;
            acc.totalSpent += trip.fare || 0;
            if (trip.rating) {
              acc.averageRating = (acc.averageRating * (acc.totalTrips - 1) + trip.rating) / acc.totalTrips;
            }
          }
          return acc;
        }, {
          totalTrips: 0,
          totalDistance: 0,
          totalSpent: 0,
          averageRating: 0
        });

        setStats(stats);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-32 bg-gray-800 rounded-xl"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-800 rounded-xl"></div>
          ))}
        </div>
        <div className="h-64 bg-gray-800 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Karşılama Kartı */}
      <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl p-6 md:p-8 shadow-lg shadow-yellow-400/20">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Hoş Geldin, {profile?.full_name}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-gray-800">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>{profile?.phone}</span>
              </div>
              {profile?.share_location && (
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4" />
                  <span>Konum Paylaşımı Açık</span>
                </div>
              )}
            </div>
          </div>
          <div className="w-16 h-16 bg-yellow-300 rounded-xl flex items-center justify-center">
            <User className="w-8 h-8 text-gray-900" />
          </div>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#24272e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Car className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-gray-400">Toplam Yolculuk</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalTrips}</p>
        </div>

        <div className="bg-[#24272e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <MapPin className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400">Toplam Mesafe</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalDistance.toFixed(1)} km</p>
        </div>

        <div className="bg-[#24272e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-400/10 rounded-lg">
              <Star className="w-5 h-5 text-yellow-400" />
            </div>
            <span className="text-gray-400">Ortalama Puan</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.averageRating.toFixed(1)}</p>
        </div>

        <div className="bg-[#24272e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400">Toplam Harcama</span>
          </div>
          <p className="text-2xl font-bold text-white">₺{stats.totalSpent.toFixed(2)}</p>
        </div>
      </div>

      {/* Son Yolculuklar */}
      <div className="bg-[#24272e] rounded-xl p-6 border border-white/5">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Son Yolculuklar</h2>
          <button className="text-yellow-400 hover:text-yellow-300 transition-colors">
            Tümünü Gör
          </button>
        </div>

        <div className="space-y-4">
          {trips.length > 0 ? (
            trips.map((trip) => (
              <div 
                key={trip.id} 
                className="bg-[#1a1d24] rounded-xl p-4 border border-white/5"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-400">
                        {new Date(trip.created_at).toLocaleDateString('tr-TR')}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        trip.status === 'completed' 
                          ? 'bg-green-500/10 text-green-400'
                          : trip.status === 'cancelled'
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-yellow-400/10 text-yellow-400'
                      }`}>
                        {trip.status === 'completed' 
                          ? 'Tamamlandı' 
                          : trip.status === 'cancelled'
                          ? 'İptal Edildi'
                          : 'Devam Ediyor'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <p className="text-white">{trip.pickup_location} → {trip.dropoff_location}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400">{trip.distance} km</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400">₺{trip.fare}</span>
                      </div>
                      {trip.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-yellow-400">{trip.rating}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">Henüz yolculuk yapmadınız.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}